import React, { useEffect, useState } from 'react';
import { useDataProvider } from 'react-admin';
import { Alert, Box, MenuItem, TextField, Typography } from '@mui/material';
import CasinoIcon from '@mui/icons-material/Casino';
import { GiftProduct, GiftProductType } from '../../../../models';

interface Props {
  product: Partial<GiftProduct>;
  onChange: (product: Partial<GiftProduct>) => void;
}

const DrawingNumberProductForm: React.FC<Props> = ({ product, onChange }) => {
  const dataProvider = useDataProvider();
  const [drawings, setDrawings] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Só sorteio com "Regras via Clube de Vantagens" pode ser vinculado aqui. No
    // sorteio legado as regras vivem em "Regras do sorteio", e usar os dois motores
    // no mesmo sorteio concede número duas vezes.
    dataProvider.getList('chains/chainId/drawings', {
      sort: { field: 'name', order: 'ASC' },
      pagination: { page: 1, perPage: 50 },
      filter: { isClubDriven: true, isArchived: false, status: 'not-finished' },
    })
      .then(({ data }) => setDrawings(data))
      .catch(() => setDrawings([]))
      .finally(() => setLoading(false));
  }, []);

  const handleDrawingSelect = (drawingId: string) => {
    const selectedDrawing = drawings.find((drawing) => drawing.id === drawingId);
    onChange({
      ...product,
      type: GiftProductType.DrawingNumber,
      drawingId: selectedDrawing?.id ?? null,
      drawingName: selectedDrawing?.name ?? null,
      // A quantidade é o multiplicador da conquista: 1 conquista = N números.
      luckyNumberAmount: product.luckyNumberAmount || 1,
    });
  };

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Esta recompensa dá ao cliente <strong>números da sorte</strong> de um sorteio que você já cadastrou.
        Cada vez que o cliente cumprir a regra deste brinde, ele recebe a quantidade de números configurada aqui —
        não um cupom.
      </Alert>

      {!loading && drawings.length === 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Nenhum sorteio disponível. Só aparecem aqui os sorteios em andamento criados com a opção
          <strong> "Regras via Clube de Vantagens"</strong> ligada.
        </Alert>
      )}

      <TextField
        select
        fullWidth
        required
        label="Sorteio vinculado"
        value={product.drawingId || ''}
        onChange={(e) => handleDrawingSelect(e.target.value)}
        disabled={loading || drawings.length === 0}
        helperText="Apenas sorteios em andamento com as regras no Clube de Vantagens."
      >
        {drawings.map((drawing) => (
          <MenuItem key={drawing.id} value={drawing.id}>
            {drawing.name}
          </MenuItem>
        ))}
      </TextField>

      <Box mt={3}>
        <TextField
          fullWidth
          required
          type="number"
          label="Quantos números da sorte esta recompensa dá"
          value={product.luckyNumberAmount ?? 1}
          onChange={(e) => onChange({
            ...product,
            type: GiftProductType.DrawingNumber,
            luckyNumberAmount: Math.max(1, parseInt(e.target.value, 10) || 1),
          })}
          inputProps={{ min: 1, step: 1 }}
          helperText="Mínimo 1. É a quantidade concedida a cada conquista do brinde."
        />
      </Box>

      {product.drawingId && (
        <Box mt={3} p={2} border="1px solid" borderColor="divider" borderRadius={2}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Sorteio selecionado:
          </Typography>
          <Box display="flex" alignItems="center" gap={2}>
            <CasinoIcon sx={{ fontSize: 32, color: '#00897b' }} />
            <Typography variant="h6">{product.drawingName || product.drawingId}</Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default DrawingNumberProductForm;
